<template>
  <div class="tw:bg-blue-200 tw:flex tw:items-start" v-if="!isPaidClient">
    <ContainerLayout><TopBanner /></ContainerLayout>
  </div>
  <div class="main-bg tw:min-h-(--tw-min-height-screen-minus-header-and-footer) tw:flex">
    <ContainerLayout class="tw:md:flex-row tw:flex-col">
      <div
        class="tw:w-full tw:md:w-[71%] tw:bg-white tw:min-h-(--tw-min-height-screen-minus-header-and-footer) tw:md:pr-8"
      >
        <!-- Horizontal notifications for mobile (max 2, side-by-side, 420px min each) -->
        <div class="tw:md:hidden">
          <SidebarNotification layout="horizontal" v-if="isMobile" />
        </div>

        <ProToPlusReactivationBanner
          v-if="isPro && isCancelledSubscription"
          :itemId="selectedAccommodationId"
          source="homepage_banner_top"
          dismissKey="pro_to_plus_banner_top_dismissed"
          class="tw:mt-8 tw-mb-6"
        />
        <PerformanceContainer :hasRateConnect="Boolean(campaignsData)" :isRateConnectEligible="isRateConnectEligible" />
        <RateConnectCPCBanner
          v-if="
            shouldShowSmartBoost && Boolean(campaignsData) && campaignsData?.campaignType === 'cpc' && isNetCpaEligible
          "
        />
        <RateConnectContainer
          v-if="isRateConnectEligible"
          :isRateConnectEligible="isRateConnectEligible"
          :hasRateConnect="Boolean(campaignsData)"
        />
        <StudioPlusContainer :isStudioPlusEligible="isStudioPlusEligible" :isCancelled="isCancelledSubscription" />

        <RateConnectContainer
          v-if="!isRateConnectEligible"
          :isRateConnectEligible="isRateConnectEligible"
          :hasRateConnect="Boolean(campaignsData)"
        />

        <ProToPlusReactivationSection
          v-if="isPro && isCancelledSubscription"
          source="homepage_banner_bottom"
          class="tw:mt-6" />
      </div>
      <div class="tw:w-full tw:md:w-[29%]">
        <SidebarContainer :isStudioPlusEligible="isStudioPlusEligible" :isRateConnectEligible="isRateConnectEligible" />
      </div>
    </ContainerLayout>
  </div>
  <FullScreenLoader v-show="initialDataLoading" />
</template>

<script setup>
import { computed, watch } from 'vue'
import { useRouter } from 'vue-router'

import StudioPlusContainer from '@/components/homePage/studioPlus/StudioPlusContainer.vue'
import ProToPlusReactivationBanner from '@/components/homePage/studioPlus/ProToPlusReactivationBannerContainer.vue'
import ProToPlusReactivationSection from '@/components/homePage/studioPlus/ProToPlusReactivationSectionContainer.vue'
import ContainerLayout from '@/layouts/ContainerLayout.vue'
import RateConnectContainer from '@/components/homePage/rateConnect/RateConnectContainer.vue'
import SidebarContainer from '@/components/homePage/sidebar/SidebarContainer.vue'
import FullScreenLoader from '@/components/base/loaders/FullScreenLoader.vue'
import TopBanner from '@/components/homePage/TopBanner.vue'
import PerformanceContainer from '@/components/homePage/performance/PerformanceContainer.vue'
import SidebarNotification from '@/components/productNotifications/sidebarNotifications/SidebarNotification.vue'
import RateConnectCPCBanner from '@/components/homePage/rateConnect/RateConnectCPCBanner.vue'

import { useStudioPlusCampaign } from '@/components/payment/queries'
import { selectedAccommodationId, useAccommodationsByOrgId } from '@/layouts/queries'
import { useCampaign, usePartners } from '@/components/rateConnect/queries'
import { RC_BLOCKED_COUNTRIES } from '@/constants/rcBlockedCountries'
import { useRCVoucher } from '@/components/rateConnect/setup/useRCVoucher'

import { useSessionValues } from '@/composables/useSessionValues'
import { useIsMobile } from '@/composables/useIsMobile'

const router = useRouter()
const { isMobile } = useIsMobile()
const {
  isLoading: isStudioPlusCampaignLoading,
  isExpiredSubscription,
  isCancelledSubscription,
  isPlus,
  isPro,
} = useStudioPlusCampaign(selectedAccommodationId)
const shouldShowSmartBoost = import.meta.env.VITE_FEATURE_VISIBLE === 'true'
const { campaignsData, isLoading: isRateConnectDataLoading } = useCampaign(selectedAccommodationId)
const { activePartnersData, isNetCpaEligible, isLoading: isPartnersDataLoading } = usePartners(selectedAccommodationId)
const initialDataLoading = computed(
  () => isStudioPlusCampaignLoading.value || isRateConnectDataLoading.value || isPartnersDataLoading.value
)
const { selectedOrganisationId, hasAdminOrSupport } = useSessionValues()
const { isFromMarketingLink } = useRCVoucher()
const { currentAccommodation } = useAccommodationsByOrgId(selectedOrganisationId)
const isBlockedCountry = computed(() => {
  const countryId = currentAccommodation.value?.countryId
  return countryId ? RC_BLOCKED_COUNTRIES.includes(countryId) : false
})
const isEnabledForRateConnect = computed(() => (activePartnersData.value ?? []).length > 0)
const isStudioPlusEligible = computed(
  () => ((!isPro.value && !isPlus.value) || isExpiredSubscription.value) && !isBlockedCountry.value
)
const isRateConnectEligible = computed(
  () => !campaignsData.value && isNetCpaEligible.value && isEnabledForRateConnect.value && !isBlockedCountry.value
)
const { studioPlusCampaign } = useStudioPlusCampaign(selectedAccommodationId)
const isPaidClient = computed(
  () =>
    Boolean(studioPlusCampaign.value) ||
    (Array.isArray(campaignsData.value) ? campaignsData.value.length > 0 : Boolean(campaignsData.value))
)

watch(
  initialDataLoading,
  loading => {
    if (!loading && isFromMarketingLink.value && isRateConnectEligible.value && hasAdminOrSupport.value) {
      router.push({ name: 'rate-connect-setup' })
    }
  },
  { immediate: true }
)
</script>

<style scoped>
.main-bg {
  background: #fff;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 1) 0%,
    rgba(255, 255, 255, 1) 50%,
    rgba(255, 255, 255, 0) 50%,
    rgba(255, 255, 255, 0) 100%
  );
  @media (max-width: 768px) {
    background: #fff;
  }
}
</style>
